/**
 * «Наложить трек» у готового ролика (28.09): доступна ли кнопка, какая версия звучит, склейка по кнопке.
 *
 * Состояние берётся с сервера — статус генерации (has_track / sound_source / track_version, services/audio_layer), а
 * не из браузера: после перезагрузки версия с треком и переключатель «Оригинал | Трек» на месте. Кнопка бесплатная,
 * модель не запускается — сервер кладёт трек генерации (карточки или свой звук) поверх ролика за секунды.
 */
import { useCallback, useEffect, useState } from 'react'
import { getTaskStatus, overlayTrack, type TrackVersion } from '../../../api/images'
import { showToast } from '../../../components/Toast'

// Статус не пришёл (сеть) — спрашиваем ещё: без ответа кнопки и переключателя нет, а версия могла уже быть.
const RETRY_DELAYS_MS = [3000, 10000, 30000]

interface TrackInfo {
	id: string
	hasTrack: boolean
	source: string | null
	version: TrackVersion | null
}

export interface ResultTrack {
	/** Можно наложить: трек был, звук — модели, версии с треком ещё нет. */
	available: boolean
	/** Готовая версия с треком. */
	version: TrackVersion | null
	/** Идёт склейка (секунды). */
	busy: boolean
	/** Сейчас показана версия с треком. */
	showTrack: boolean
	/** Клиент сам переключал версии — плеер можно запускать со звуком (жест уже был). */
	heard: boolean
	select: (track: boolean) => void
	overlay: () => Promise<void>
}

export function useResultTrack(requestId: string | null | undefined): ResultTrack {
	const [info, setInfo] = useState<TrackInfo | null>(null)
	const [busy, setBusy] = useState(false)
	// Выбор версии и «клиент уже слушал» — у КОНКРЕТНОГО ролика: следующий результат снова начинает с оригинала и без
	// звука (иначе после одного переключения каждый новый ролик сам заигрывал бы со звуком).
	const [view, setView] = useState<{ id: string; track: boolean } | null>(null)

	useEffect(() => {
		if (!requestId) return
		let alive = true
		let timer: number | undefined
		const load = (attempt: number) => {
			getTaskStatus(requestId)
				.then((s) => {
					if (!alive) return
					setInfo({ id: requestId, hasTrack: !!s.has_track, source: s.sound_source ?? null, version: s.track_version ?? null })
				})
				.catch(() => {
					// нет ответа — пробуем ещё пару раз; не вышло — кнопки просто не будет, ролик показывается как есть
					if (alive && attempt < RETRY_DELAYS_MS.length) {
						timer = window.setTimeout(() => load(attempt + 1), RETRY_DELAYS_MS[attempt])
					}
				})
		}
		load(0)
		return () => {
			alive = false
			if (timer !== undefined) window.clearTimeout(timer)
		}
	}, [requestId])

	const current = info && info.id === requestId ? info : null

	const overlay = useCallback(async () => {
		if (!requestId || busy) return
		setBusy(true)
		try {
			const r = await overlayTrack(requestId)
			setInfo((prev) => ({
				id: requestId,
				hasTrack: true,
				source: prev?.id === requestId ? prev.source : null,
				version: { request_id: r.request_id, result_url: r.result_url },
			}))
			setView({ id: requestId, track: true })
		} catch (e) {
			const message = (e as { message?: string } | null)?.message
			showToast(message || 'Не удалось наложить трек — попробуйте ещё раз', 'error')
		} finally {
			setBusy(false)
		}
	}, [requestId, busy])

	const select = useCallback(
		(track: boolean) => {
			if (requestId) setView({ id: requestId, track })
		},
		[requestId],
	)

	const mine = !!requestId && view?.id === requestId
	return {
		available: !!current && current.hasTrack && !current.source && !current.version,
		version: current?.version ?? null,
		busy,
		showTrack: mine && !!view?.track && !!current?.version,
		heard: mine,
		select,
		overlay,
	}
}
